import type { ReactNode, RefObject } from 'react';
import { X } from '@phosphor-icons/react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '~/components/ui/dialog';

// Shared visual frame for the app editor's two allowlist pickers
// (`tool-picker-dialog.tsx`, `agent-picker-dialog.tsx`) — FRONT-2101.
// Not `ZenDialog`: its header has no room for a real `DialogTitle` beside the
// eyebrow, so this composes Radix directly, like the Settings modal does.
export interface PickerDialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eyebrow: string; // Words after "Eddy · " in the header, e.g. "app capabilities".
  // FRONT-2501: replaces the tile + eyebrow on the left of the header, for a
  // dialog that belongs to a window with its own lockup (the Builder's
  // "EDDY / Builder"), so the dialog reads as part of that window.
  brand?: ReactNode;
  title: ReactNode; // Accessible name, also rendered visibly.
  description?: ReactNode; // Radix `DialogDescription`, also the visible intro copy.
  children: ReactNode; // Scrolling body — search box, grouped list, stale block, etc.
  footer?: ReactNode; // Pinned below the scrolling body — `ActionBar` in both callers.
  initialFocusRef?: RefObject<HTMLElement | null>; // Else Radix focuses this shell's own close button first.
}

// Copied from Settings' own local, unexported `EddyMark` (the chat window
// carried the same tile until FRONT-2379 moved it to the real wordmark).
function MarkTile() {
  return (
    <span
      aria-hidden="true"
      className="size-[22px] rounded-md bg-foreground text-background inline-flex items-center justify-center font-semibold text-xs shrink-0"
    >
      E
    </span>
  );
}

export function PickerDialogShell({
  open,
  onOpenChange,
  eyebrow,
  brand,
  title,
  description,
  children,
  footer,
  initialFocusRef,
}: PickerDialogShellProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          if (initialFocusRef?.current) {
            event.preventDefault();
            initialFocusRef.current.focus();
          }
        }}
        className="flex max-h-[85dvh] w-full flex-col gap-0 overflow-hidden border border-border bg-card p-0 text-foreground sm:max-w-3xl"
        style={{
          borderRadius: 'var(--radius-xl-ds)',
          boxShadow: 'var(--shadow-lg)',
        }}
        overlayStyle={{
          background: 'rgba(20, 20, 26, 0.42)',
          backdropFilter: 'blur(10px) saturate(0.9)',
          WebkitBackdropFilter: 'blur(10px) saturate(0.9)',
        }}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-border px-[22px] py-3.5">
          {brand ?? (
            <>
              <MarkTile />
              <span className="hidden shrink-0 truncate text-[length:var(--text-xs)] text-muted-foreground sm:block">
                EDDY · {eyebrow}
              </span>
            </>
          )}
          {/* Under a window's own lockup the title sits where that window puts
              its project name, so it takes that name's size, not a heading's. */}
          <DialogTitle
            className={`min-w-0 flex-1 truncate font-semibold text-foreground ${
              brand ? 'text-[length:var(--text-sm)]' : 'text-[length:var(--text-lg)]'
            }`}
          >
            {title}
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-[10px] text-foreground transition-colors hover:bg-muted"
            >
              <X size={18} />
            </button>
          </DialogClose>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-[22px] py-4">
          {description !== undefined && (
            <DialogDescription className="text-sm text-muted-foreground">
              {description}
            </DialogDescription>
          )}
          {children}
        </div>

        {footer && <div className="shrink-0">{footer}</div>}
      </DialogContent>
    </Dialog>
  );
}
